import React from 'react';
import { formatToDisplayDate, parseDisplayDate } from '../utils/dateFormatter';

/**
 * CommonDateInput - A reusable date input component
 * 
 * @param {string} name - Field name
 * @param {string} value - Date value (ISO format or display format)
 * @param {function} onChange - Change handler
 * @param {boolean} readOnly - Whether the field is read-only
 * @param {boolean} disabled - Whether the field is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} className - Additional CSS classes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} showTime - Whether to show time (for display only in read-only mode)
 * @param {string} id - Input ID
 * @param {string} minDate - Minimum date (YYYY-MM-DD format) - defaults to today
 * @param {string} maxDate - Maximum date (YYYY-MM-DD format)
 * @param {boolean} allowPastDates - Allow selecting past dates (default: false)
 */
const CommonDateInput = ({
  name,
  value,
  onChange,
  readOnly = false,
  disabled = false,
  required = false,
  className = '',
  placeholder = 'DD-MM-YYYY',
  showTime = false,
  id,
  minDate,
  maxDate,
  allowPastDates = false, // New prop to control past date selection
  ...rest
}) => {
  // Format value for display or input
  const getDisplayValue = () => {
    if (!value) return '';
    
    // If read-only, show formatted date (with or without time)
    if (readOnly || disabled) {
      return formatToDisplayDate(value, showTime, '24h', true); // true = form field format
    }
    
    // For editable input, convert to YYYY-MM-DD for date picker
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch {
      // If conversion fails, return empty
    }
    
    return '';
  };

  // Handle change event
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    if (!newValue) {
      // Pass empty value
      onChange({ target: { name, value: '' } });
      return;
    }
    
    // Convert date input value (YYYY-MM-DD) to ISO format for storage
    try {
      const date = new Date(newValue);
      if (!isNaN(date.getTime())) {
        const isoValue = date.toISOString();
        onChange({ target: { name, value: isoValue } });
      }
    } catch (error) {
      console.error('Date conversion error:', error);
      onChange(e);
    }
  };

  // Calculate min and max dates
  const getMinDate = () => {
    if (minDate) return minDate;
    if (!allowPastDates) {
      // Set minimum date to today if past dates are not allowed
      return new Date().toISOString().split('T')[0];
    }
    return undefined;
  };

  const getMaxDate = () => {
    return maxDate;
  };

  // If read-only or disabled, show as text input with formatted date
  if (readOnly || disabled) {
    return (
      <input
        id={id || name}
        type="text"
        name={name}
        value={getDisplayValue()}
        className={`form-control ${className}`}
        readOnly
        disabled={disabled}
        placeholder={showTime ? 'DD-MM-YYYY, HH:MM' : placeholder}
        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
        {...rest}
      />
    );
  }

  // Editable date picker
  return (
    <input
      id={id || name}
      type="date"
      name={name}
      value={getDisplayValue()}
      onChange={handleChange}
      className={`form-control ${className}`}
      required={required}
      min={getMinDate()}
      max={getMaxDate()}
      {...rest}
    />
  );
};

export default CommonDateInput;
